import { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap/gsap-core';
import { CSSPlugin } from 'gsap/CSSPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useMagnetic } from '../hooks/useMagnetic';
import { prefersReducedMotion } from '../lib/motion';
import { ChevronDown, Plane, Building2, Calendar, Route, Zap } from 'lucide-react';

gsap.registerPlugin(CSSPlugin, ScrollTrigger);

// --------------------------------------------------------------------------
// Orchestration visual — new centerpiece motif depicting Voyara AI as the
// coordination core between flights, stays, schedules, and routing.
// --------------------------------------------------------------------------
const ORCHESTRATION_NODES = [
  { Icon: Plane, label: 'Flights', top: '8%', left: '50%', lineTo: [50, 8] },
  { Icon: Building2, label: 'Stays', top: '50%', left: '92%', lineTo: [92, 50] },
  { Icon: Calendar, label: 'Schedule', top: '92%', left: '50%', lineTo: [50, 92] },
  { Icon: Route, label: 'Routing', top: '50%', left: '8%', lineTo: [8, 50] },
] as const;

function OrchestrationVisual() {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  return (
    <div className="relative w-full max-w-[420px] mx-auto aspect-square">
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" aria-hidden="true">
        {ORCHESTRATION_NODES.map(({ lineTo }, i) => {
          const [x, y] = lineTo;
          const active = activeIdx === i;
          return (
            <line
              key={`${x}-${y}`}
              x1="50" y1="50" x2={x} y2={y}
              stroke={active ? 'rgba(140,170,255,0.9)' : 'rgba(100,140,255,0.35)'}
              strokeWidth={active ? 0.7 : 0.4}
              strokeDasharray="2 2"
              vectorEffect="non-scaling-stroke"
              className="orchestration-line transition-[stroke,stroke-width] duration-300"
            />
          );
        })}
      </svg>

      {/* Core node */}
      <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full liquid-glass-strong flex items-center justify-center z-10 transition-transform duration-300 ${activeIdx !== null ? 'scale-110' : ''}`}>
        <div className={`absolute inset-0 rounded-full blur-xl transition-colors duration-300 ${activeIdx !== null ? 'bg-blue-500/25' : 'bg-blue-500/10'}`} />
        <Zap className="w-7 h-7 text-blue-300 relative" aria-hidden="true" />
      </div>

      {/* Satellite nodes */}
      {ORCHESTRATION_NODES.map(({ Icon, label, top, left }, i) => (
        <div
          key={label}
          className="absolute -translate-x-1/2 -translate-y-1/2"
          style={{ top, left }}
          onMouseEnter={() => setActiveIdx(i)}
          onMouseLeave={() => setActiveIdx((cur) => (cur === i ? null : cur))}
        >
          <div className={`w-12 h-12 rounded-full liquid-glass flex items-center justify-center transition-all duration-300 ${
            activeIdx === i ? 'scale-110 bg-blue-500/10 border-blue-400/30' : ''
          }`}>
            <Icon className={`w-4 h-4 transition-colors duration-300 ${activeIdx === i ? 'text-blue-200' : 'text-primary/70'}`} aria-hidden="true" />
          </div>
          <span
            className={`absolute left-1/2 -translate-x-1/2 top-full mt-2 whitespace-nowrap text-[10px] font-semibold tracking-[0.2em] uppercase text-blue-200/90 transition-opacity duration-300 pointer-events-none ${
              activeIdx === i ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {label}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function Hero() {
  const sectionRef = useRef<HTMLElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const magnetic = useMagnetic(0.2);

  useEffect(() => {
    if (!sectionRef.current || !bgRef.current || prefersReducedMotion()) return;

    const ctx = gsap.context(() => {
      // Background slow zoom drift
      gsap.to(bgRef.current, {
        scale: 1.08,
        duration: 30,
        ease: 'none',
        repeat: -1,
        yoyo: true,
      });

      // Parallax on scroll
      gsap.to(bgRef.current, {
        y: 150,
        opacity: 0.2,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: 1.5,
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);



  const scrollToWaitlist = () => {
    const el = document.getElementById('waitlist');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section ref={sectionRef} className="relative min-h-screen min-h-[100dvh] flex items-center justify-center overflow-hidden">
      {/* ── Cinematic background layers ── */}
      <div ref={bgRef} className="absolute inset-[-10%] pointer-events-none">
        <div className="absolute inset-0 bg-black" />

        {/* Ultra subtle grid */}
        <div className="absolute inset-0 opacity-[0.012]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)
            `,
            backgroundSize: '100px 100px',
          }}
        />

        {/* Light rays */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-[0.15]">
          <div className="absolute top-[-20%] left-[20%] w-[1000px] h-[200%] origin-top opacity-0"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(140, 170, 255, 0.03), transparent)',
              animation: 'lightRay 15s ease-in-out infinite',
            }}
          />
          <div className="absolute top-[-10%] right-[30%] w-[800px] h-[200%] origin-top opacity-0"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(160, 180, 255, 0.02), transparent)',
              animation: 'lightRay 20s ease-in-out infinite 5s',
            }}
          />
        </div>

        {/* AI Core Visual */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] pointer-events-none opacity-[0.03]">
          <svg viewBox="0 0 800 800" className="w-full h-full animate-[coreRotate_60s_linear_infinite]" style={{ transformOrigin: '50% 50%' }} aria-hidden="true">
            <circle cx="400" cy="400" r="380" fill="none" stroke="#fff" strokeWidth="1" strokeDasharray="4 12" />
            <circle cx="400" cy="400" r="340" fill="none" stroke="#fff" strokeWidth="0.5" strokeDasharray="1 8" />
            <circle cx="400" cy="400" r="280" fill="none" stroke="#fff" strokeWidth="1.5" strokeDasharray="20 40 10 40" />
            <circle cx="400" cy="400" r="200" fill="none" stroke="#fff" strokeWidth="0.5" />
            <circle cx="400" cy="200" r="4" fill="#fff" />
            <circle cx="400" cy="600" r="4" fill="#fff" />
            <circle cx="200" cy="400" r="4" fill="#fff" />
            <circle cx="600" cy="400" r="4" fill="#fff" />
            <circle cx="400" cy="400" r="60" fill="none" stroke="#fff" strokeWidth="1" />
          </svg>
        </div>

        {/* Scanlines */}
        <div className="absolute inset-0 opacity-[0.015] pointer-events-none"
          style={{
            background: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(255,255,255,0.02) 3px, rgba(255,255,255,0.02) 4px)',
          }}
        />
      </div>

      {/* ── Cinematic vignette ── */}
      <div className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 70% 60% at 50% 45%, transparent 0%, rgba(0,0,0,0.5) 60%, rgba(0,0,0,1) 100%)`,
        }}
      />

      {/* ── Content ── */}
      <div className="relative z-10 global-container max-w-[1400px]">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-16 items-center">
          <div className="text-center lg:text-left animate-[floatSlow_8s_ease-in-out_infinite]">
            {/* Pill badge */}
            <div className="mb-12 animate-hero-fade-up delay-600">
              <span className="inline-flex items-center gap-2.5 liquid-glass rounded-full px-6 py-2.5 text-[11px] font-semibold tracking-[0.3em] uppercase text-primary shadow-[0_0_30px_rgba(255,255,255,0.03)] border border-white/10">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-blue-400" />
                </span>
                Introducing Voyara AI
              </span>
            </div>

            {/* ── Hero Heading ── */}
            <div className="relative mb-10">
              <h1 className="font-serif relative z-10 text-balance" style={{
                fontSize: 'clamp(2.2rem, 6.5vw, 6rem)',
                lineHeight: '0.95',
                letterSpacing: '-0.04em',
              }}>
                {/* Line 1 */}
                <span className="block text-primary/95 animate-hero-fade-up delay-1000">
                  Eclipse Group is building
                </span>

                {/* Line 2 */}
                <span className="block mt-[0.05em] animate-hero-fade-up delay-1150">
                  <span className="text-primary/95">the future of </span>
                  <span style={{
                    background: 'linear-gradient(90deg, #FFFFFF 0%, rgba(255,255,255,0.4) 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}>
                    intelligent travel.
                  </span>
                </span>
              </h1>
            </div>

            {/* Subheading */}
            <p className="text-secondary text-[clamp(1rem,1.8vw,1.25rem)] leading-relaxed max-w-2xl mx-auto lg:mx-0 font-medium mb-16 tracking-tight text-pretty animate-hero-fade-up delay-300">
              An AI-powered travel intelligence system designed to transform planning, booking,
              coordination, and movement into one seamless experience.
            </p>

            {/* ── CTA Button ── */}
            <div className="flex justify-center lg:justify-start animate-hero-fade-up delay-350">
              <div
                ref={magnetic.ref}
                onMouseMove={magnetic.handleMouseMove}
                onMouseLeave={magnetic.handleMouseLeave}
                className="group relative"
              >
                <div className="absolute inset-0 bg-blue-500/20 blur-2xl rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <button
                  onClick={scrollToWaitlist}
                  className="relative px-12 py-5 rounded-2xl bg-white/10 border border-white/20 text-primary text-[14px] font-bold tracking-[0.2em] uppercase hover:bg-white/15 hover:border-white/30 hover:shadow-[0_0_50px_rgba(120,140,255,0.15)] transition-all duration-600 cursor-pointer overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-[scanSweep_2s_ease-in-out_infinite]" />
                  Request Early Access
                </button>
              </div>
            </div>

            <p className="mt-10 text-[12px] text-accent font-medium tracking-[0.08em] animate-hero-fade-up delay-400">
              Limited early access for corporate travelers, creators, and high-frequency flyers.
            </p>
          </div>

          {/* Orchestration visual — desktop only, keeps mobile hero focused on copy */}
          <div className="hidden lg:block animate-hero-fade-up delay-1000">
            <OrchestrationVisual />
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        
        
        
        className="absolute bottom-12 left-1/2 -translate-x-1/2 z-10"
      >
        <div 
          
          
          className="flex flex-col items-center gap-3"
        >
          <span className="text-[9px] tracking-[0.35em] uppercase text-[#f5f5f5]/15 font-light">Scroll</span>
          <ChevronDown className="w-4 h-4 text-[#f5f5f5]/15" aria-hidden="true" />
        </div>
      </div>
    </section>
  );
}
